import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useEffect, useRef, useState } from 'react'
import { useLocation } from 'react-router-dom'
import { cancelOrder, fetchOrder, fetchOrders } from '../api/orders'
import type { ApiOrder } from '../api/types'
import { ONLINE_PAYMENT_EXPIRED, ORDER_LIST_LIMIT, ORDER_UI } from '../constants/orders'
import { CHECKOUT_SETTLE } from '../constants/payments'
import { QUERY_KEYS } from '../constants/query'
import { toApiMessage } from '../helpers/api-error'
import { siblingCheckoutOrders } from '../helpers/express'
import {
  isUnpaidOnlineOrder,
  isUnpaidOnlinePaymentExpired,
  onlinePaymentTimeoutMs,
  readOrderPlacedState,
  replaceOrder,
} from '../helpers/orders'
import { payOnlineCheckout } from '../helpers/payment-gateway'
import { useAuth } from './useAuth'

type UseOrderDetailResult = {
  order: ApiOrder | null
  siblings: ApiOrder[]
  loading: boolean
  error: string | null
  cancelling: boolean
  paying: boolean
  justPlaced: boolean
  placedSplit: boolean
  needsPayment: boolean
  cancel: (reason?: string) => Promise<boolean>
  pay: () => Promise<boolean>
}

export function useOrderDetail(id: string | undefined): UseOrderDetailResult {
  const { isAuthenticated } = useAuth()
  const queryClient = useQueryClient()
  const { state } = useLocation()
  const placed = readOrderPlacedState(state)
  const [cancelling, setCancelling] = useState(false)
  const [paying, setPaying] = useState(false)
  const [actionError, setActionError] = useState<string | null>(null)
  const expiredAttempt = useRef<string | null>(null)
  const orderQuery = useQuery({
    queryKey: QUERY_KEYS.order(id ?? ''),
    queryFn: () => fetchOrder(id!),
    enabled: Boolean(isAuthenticated && id),
  })
  const ordersQuery = useQuery({
    queryKey: QUERY_KEYS.orders,
    queryFn: () => fetchOrders({ limit: ORDER_LIST_LIMIT }),
    enabled: Boolean(isAuthenticated && orderQuery.data?.checkoutId),
  })
  const error = !id
    ? ORDER_UI.notFound
    : actionError ??
      (orderQuery.error
        ? toApiMessage(orderQuery.error, ORDER_UI.notFound)
        : null)

  async function cancel(reason?: string) {
    if (!id) return false
    setCancelling(true)
    setActionError(null)
    try {
      const next = await cancelOrder(id, reason)
      queryClient.setQueryData(QUERY_KEYS.order(id), next)
      queryClient.setQueryData<ApiOrder[]>(QUERY_KEYS.orders, (current) =>
        replaceOrder(current, next),
      )
      return true
    } catch (err) {
      setActionError(toApiMessage(err, ORDER_UI.loadError))
      return false
    } finally {
      setCancelling(false)
    }
  }

  useEffect(() => {
    const current = orderQuery.data
    if (!current || expiredAttempt.current === current.id) return
    if (!isUnpaidOnlinePaymentExpired(current)) return
    expiredAttempt.current = current.id
    void cancel(ONLINE_PAYMENT_EXPIRED)
  }, [orderQuery.data])

  async function pay() {
    const checkoutId = orderQuery.data?.checkoutId
    if (!checkoutId || !orderQuery.data) {
      setActionError(ORDER_UI.paymentError)
      return false
    }
    setPaying(true)
    setActionError(null)
    try {
      const settled = await payOnlineCheckout({
        checkoutId,
        gateway: orderQuery.data.paymentGateway,
        timeoutMs: onlinePaymentTimeoutMs(orderQuery.data.createdAt),
      })
      if (settled.status === CHECKOUT_SETTLE.expired) {
        await cancel(ONLINE_PAYMENT_EXPIRED)
        setActionError(ORDER_UI.paymentExpired)
        return false
      }
      if (!settled.checkout) return false
      for (const next of settled.checkout.orders) {
        queryClient.setQueryData(QUERY_KEYS.order(next.id), next)
        queryClient.setQueryData<ApiOrder[]>(QUERY_KEYS.orders, (current) =>
          replaceOrder(current, next),
        )
      }
      void queryClient.invalidateQueries({ queryKey: QUERY_KEYS.orders })
      return true
    } catch (err) {
      setActionError(toApiMessage(err, ORDER_UI.paymentError))
      return false
    } finally {
      setPaying(false)
    }
  }

  const order = isAuthenticated && id ? (orderQuery.data ?? null) : null
  const siblings = order
    ? siblingCheckoutOrders(ordersQuery.data, order, placed?.orderIds)
    : []

  return {
    order,
    siblings,
    loading:
      Boolean(isAuthenticated && id) &&
      orderQuery.isPending &&
      !orderQuery.data &&
      !orderQuery.error,
    error,
    cancelling,
    paying,
    justPlaced: Boolean(placed),
    placedSplit: Boolean(placed && (placed.orderIds?.length ?? 0) > 1),
    needsPayment: Boolean(order && isUnpaidOnlineOrder(order)),
    cancel,
    pay,
  }
}
